import {
  AnsweredWithoutMetricAnswerIcon,
  BodyMedium,
  BoxWithImage,
  DeleteIcon,
  Label,
  LabelColor,
  LabelSize,
  NoMetricsQuestionIcon,
  NoSurveyIcon,
  NotDeliveredIcon,
  PlainButton,
  Tooltip,
  theme,
  HourGlassIcon,
  DeprecatedSendIcon,
  DeprecatedAlertIcon,
  DeprecatedNoIcon,
  DeprecatedCommentWarningIcon,
} from '@arnold/common';
import { ReactComponent as EmptyTable } from '@arnold/common/lib/assets/images/EmptyTable.svg';
import { getLocalizedDateWithoutLeadingZeroes } from '@arnold/core';
import styled from '@emotion/styled/macro';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { LoadingAnimation } from '../../components/LoadingAnimation';
import { RemoveRespondentModal } from '../../components/modals/RemoveRespondentModal';
import { UpdateRespondent } from '../../components/modals/UpdateRespondent';
import {
  Features,
  GetProcessReportRespondentsQuery,
  ReportSequenceRespondentDetailQueryQuery,
  ReportSequenceSubscriptionSubscription,
  SurveyStatus,
} from '../../generated/hooks';
import { getTopicGroupTranslation } from '../../lib/reportHelper';
import { useIsMobileView } from './ResponsiveSwitcher';
import useRespondentsFeelings from './useRespondentsFeelings';
import { setScrollToParam, useScrollToRespondentTable } from '../../lib/reports/reportUtils';
import { usePushReportEvent } from '../ReportGTMContext';
import React, { Fragment, useRef } from 'react';

type ReportSequence =
  | NonNullable<ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail']>
  | NonNullable<NonNullable<ReportSequenceSubscriptionSubscription>['getProcessReport']>;

interface IProps {
  reportSequence: ReportSequence;
  groupedRespondents: GetProcessReportRespondentsQuery['getProcessReportRespondents']['data'];
  accessToken: string;
  encryptedRespondentId?: string;
  urlParams: string;
  respondentLink?: boolean;
  publicAccess?: boolean;
  loading?: boolean;
  showDeleteButton?: boolean;
  onReload: (shoudReload?: boolean) => void;
  createDetailUrl?: (topicGroupId: string) => string;
  reloadRespondentsData?: () => void;
  isDetail?: boolean;
  currentPage?: number;
}

const ContentsLink = styled(Link)`
  display: contents;
  color: inherit;
  font-weight: inherit;
  text-decoration: none;
  &:hover {
    color: inherit;
    font-weight: inherit;
  }
`;

const Container = styled.div`
  padding: ${theme.spacing.g} ${theme.spacing.f};
  display: inline-flex;
  flex-direction: column;
  max-width: calc(60rem - 32px);
  overflow-x: auto;
`;

const Row = styled.div`
  color: ${theme.colors.text.primary};
  display: inline-flex;
  padding: 8px;
  font-size: 11px;
  transition: background 0.25s ease;
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};

  &:hover {
    background: ${theme.colors.backgroundCover.hover};
  }
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${theme.breakpoint.small}) {
    justify-content: space-between;
  }
`;

const HeaderRow = styled.div`
  display: inline-flex;
  padding: ${theme.spacing.d};
  font-size: 11px;
  color: ${theme.colors.text.secondary};
  background-color: ${theme.colors.backgroundBasic.default};
  @media (max-width: ${theme.breakpoint.small}) {
    padding: ${theme.spacing.c};
    border-radius: ${theme.borderRadius.l};
    justify-content: space-between;
    & > div {
      min-width: 100px;
      justify-content: flex-end;
    }
  }
`;

const Cell = styled.div`
  height: 44px;
  padding: ${theme.spacing.d};

  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin-left: ${theme.spacing.d};
  &:first-child {
    margin-left: 0;
  }
`;

const BaseDateCell = styled(Cell)`
  width: 110px;
  min-width: 110px;
  justify-content: flex-start;
`;

const DateCell = styled(BaseDateCell)`
  align-items: center;
  @media (max-width: ${theme.breakpoint.small}) {
    align-items: flex-start;
    justify-content: flex-end;
    padding-top: 0;
  }
`;

const NameSpan = styled.div`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  display: inline-block;
`;

const TeamSpan = styled(NameSpan)`
  position: relative;
  top: -4px;
  color: ${theme.colors.text.secondary};
`;

interface RespondentCellProps {
  withoutPadding?: boolean;
}

const RespondentCell = styled.div<RespondentCellProps>`
  width: 180px;
  min-width: 180px;
  padding: ${({ withoutPadding }) => (withoutPadding ? `0 ${theme.spacing.d}` : theme.spacing.d)};
  display: flex;
  flex-direction: column;
  @media (max-width: ${theme.breakpoint.small}) {
    width: inherit;
  }
`;

const RespondentHeadCell = styled(RespondentCell)`
  padding: ${theme.spacing.d};
  width: 180px;
  min-width: 180px;
`;

const BaseAnswerCell = styled(Cell)`
  width: 64px;
  padding: 8px 0px;
  text-align: center;
  flex-shrink: 0;
`;

const AnswerCell = styled(BaseAnswerCell)`
  padding: ${theme.spacing.a};
  align-items: center;
  display: flex;
  flex-direction: column;
  color: ${theme.colors.text.secondary};
`;

const SplitCell = styled(AnswerCell)`
  padding: 0;
  flex-direction: row;
  border-radius: ${theme.borderRadius.m};
  overflow: hidden;
`;
type HalfOfCellProps = { light?: boolean };
const HalfOfCell = styled.div`
  padding: 10px 0;
  display: block;
  background: ${(props: HalfOfCellProps) =>
    props.light ? theme.colors.backgroundCover.default : theme.colors.actionInvertedPrimary.default};
  border: 1px solid ${theme.colors.actionInvertedPrimary.default};
  align-items: center;
  justify-content: center;
  width: 50%;
  & svg {
    width: 18px;
    height: 18px;
  }
  & > div {
    margin-left: 0px;
  }
  &:hover {
    background: ${(props: HalfOfCellProps) =>
      props.light ? theme.colors.backgroundCover.hover : theme.colors.actionInvertedPrimary.hover};
  }
`;

const StyledAlertIcon = styled(DeprecatedAlertIcon)`
  &:hover {
    path {
      fill: ${theme.colors.chart.alert[0]};
    }
  }
`;

const StyledCommentWarningIcon = styled(DeprecatedCommentWarningIcon)`
  &:hover {
    path {
      fill: ${theme.colors.chart.warning[0]};
    }
  }
`;

const StyledNoIcon = styled(DeprecatedNoIcon)`
  &:hover {
    path {
      fill: ${theme.colors.iconText.disabled};
    }
  }
  flex-shrink: 0;
`;

const StyledCloseIcon = styled(PlainButton)`
  & {
    path {
      fill: ${theme.colors.brand.primary};
    }
  }
  &:disabled {
    path {
      fill: ${theme.colors.iconText.disabled} !important;
    }
  }
`;

const StyledAnswerCell = styled.div`
  display: flex;
  flex-flow: column;
  gap: ${theme.spacing.c};
`;

const StyledLabel = styled(Label)`
  text-align: center;
  margin: 0 ${theme.spacing.b};
`;

interface IColoredAnswerCellProps {
  isPerfect?: boolean;
  isWarning?: boolean;
  isAlert?: boolean;
}

const BaseColorAnswerCell = styled(AnswerCell)`
  padding: ${theme.spacing.d};
  flex-direction: row;
  border-radius: ${theme.borderRadius.m};
  cursor: pointer;
  color: ${theme.colors.text.primary};
  transition: background 0.25s ease;
  background: ${(props: IColoredAnswerCellProps) =>
    props.isAlert
      ? theme.colors.emotionDanger.background
      : props.isPerfect
        ? theme.colors.emotionSuccess.background
        : props.isWarning
          ? theme.colors.emotionWarning.background
          : theme.colors.actionInvertedPrimary.default};

  &:hover {
    background: ${(props: IColoredAnswerCellProps) =>
      props.isAlert
        ? theme.colors.emotionDanger.backgroundHover
        : props.isPerfect
          ? theme.colors.emotionSuccess.backgroundHover
          : props.isWarning
            ? theme.colors.emotionWarning.backgroundHover
            : theme.colors.actionInvertedPrimary.hover};
  }
  &:focus {
    background: ${(props: IColoredAnswerCellProps) =>
      props.isAlert
        ? theme.colors.emotionDanger.backgroundActive
        : props.isPerfect
          ? theme.colors.emotionSuccess.backgroundActive
          : props.isWarning
            ? theme.colors.emotionWarning.backgroundActive
            : theme.colors.actionInvertedPrimary.focused};
  }
  &:active {
    background: ${(props: IColoredAnswerCellProps) =>
      props.isAlert
        ? theme.colors.emotionDanger.backgroundActive
        : props.isPerfect
          ? theme.colors.emotionSuccess.backgroundActive
          : props.isWarning
            ? theme.colors.emotionWarning.backgroundActive
            : theme.colors.actionInvertedPrimary.active};
  }
`;

const StyledTooltip = styled(Tooltip)`
  margin: 0 ${theme.spacing.c};
  display: inline;
`;

const tooltipProps: { animation: 'fade'; multiple: boolean } = {
  animation: 'fade',
  multiple: false,
};

const UnreadMessagesLabel = styled(Label)`
  @media (max-width: ${theme.breakpoint.small}) {
    margin-bottom: ${theme.spacing.d};
  }
`;

const MobileAlerts = styled.div`
  margin-bottom: ${theme.spacing.d};
  & > div {
    margin-right: ${theme.spacing.d};
    font-size: ${theme.typography.body.small.regular.fontSize};
    color: ${theme.colors.text.secondary};
    letter-spacing: 0.5px;
    svg {
      margin-right: ${theme.spacing.c};
    }
  }
`;

const LinkedBaseColorAnswerCell = (
  props: React.ComponentProps<typeof BaseColorAnswerCell> & React.ComponentProps<typeof Link>,
) => (
  <BaseColorAnswerCell
    as={Link}
    style={{ textDecoration: 'none', color: 'inherit', fontWeight: 'inherit' }}
    {...props}
  />
);
const LinkedHalfOfCell = (props: React.ComponentProps<typeof HalfOfCell> & React.ComponentProps<typeof Link>) => (
  <HalfOfCell as={Link} style={{ textDecoration: 'none', color: 'inherit', fontWeight: 'inherit' }} {...props} />
);

const LinkedAnswerCell = (props: React.ComponentProps<typeof AnswerCell> & React.ComponentProps<typeof Link>) => (
  <AnswerCell
    as={Link}
    style={{ textDecoration: 'none', color: theme.colors.text.secondary, fontWeight: 'inherit' }}
    {...props}
  />
);

const RespondentsFeelings = (props: IProps) => {
  const isMobile = useIsMobileView();
  const pushReportEvent = usePushReportEvent();
  const {
    reportSequence,
    groupedRespondents,
    accessToken,
    urlParams,
    respondentLink,
    publicAccess,
    loading,
    showDeleteButton,
    onReload,
    reloadRespondentsData,
    isDetail,
    currentPage = 1,
  } = props;
  const {
    respondents,
    showModal,
    editRespondent,
    setEditRespondent,
    respondentToDelete,
    modal,
    dateFrom,
    isHiring,
    clicked,
    containsAnonymity,
    setModal,
  } = useRespondentsFeelings(props);

  const { t, i18n } = useTranslation('Report');
  const history = useHistory();
  const accessPath = publicAccess ? 'process-results' : 'report-sequence';
  const respondentTableRef = useRef<HTMLDivElement>(null);
  useScrollToRespondentTable(respondentTableRef);

  const renderAnswerAlerts = (maxWeight: number, includeAlertDataCy = false) => (
    <>
      {maxWeight === 1 && (
        <StyledTooltip {...tooltipProps} title={t('alertsTooltip')}>
          <StyledCommentWarningIcon data-cy="report-respondents-cell-warning" />
        </StyledTooltip>
      )}
      {maxWeight > 1 && (
        <StyledTooltip {...tooltipProps} title={t('alertsTooltip')}>
          {' '}
          <StyledAlertIcon data-cy={includeAlertDataCy ? 'report-respondents-cell-alert' : undefined} />
        </StyledTooltip>
      )}
    </>
  );

  return (
    <>
      {editRespondent && (
        <UpdateRespondent
          show
          respondent={editRespondent.respondent}
          contact={editRespondent.contact}
          organizationId={reportSequence.metadata.organization.id}
          cancel={() => {
            setEditRespondent(null);
          }}
          onComplete={() => {
            setEditRespondent(null);
            if (reloadRespondentsData) {
              reloadRespondentsData();
            } else {
              onReload(false);
            }
          }}
          accessToken={accessToken}
          defaultContryCallingCode={reportSequence.metadata.organization.config?.countryCallingCode}
          isEmployeeManagementDisabled={reportSequence.metadata.organization.featureNames?.includes(
            Features.DisableEmployeeManagement,
          )}
        />
      )}
      <Container
        ref={respondentTableRef}
        className="bg-white rounded-lg shadow-sm"
        data-cy="report-onb-respondents-table"
      >
        <HeaderRow>
          {isMobile ? (
            <>
              <RespondentHeadCell>
                {t('respondent')} <br /> {isHiring ? t('position') : t('team')}
              </RespondentHeadCell>
              <BaseDateCell>{isHiring ? t('hiringFistSurveyAt') : t('addedOn')}</BaseDateCell>
            </>
          ) : (
            <>
              <BaseDateCell>{isHiring ? t('hiringFistSurveyAt') : t('addedOn')}</BaseDateCell>
              <RespondentHeadCell>
                {t('respondent')} <br /> {isHiring ? t('position') : t('team')}
              </RespondentHeadCell>
              {isHiring && <BaseDateCell>{t('recruiter')}</BaseDateCell>}
              {isHiring && <BaseDateCell>{t('hiringState')}</BaseDateCell>}
              {reportSequence.surveyGroup?.steps.map((step) => {
                const topicGroupName = getTopicGroupTranslation(
                  step!.processStep.stepTopicGroup,
                  reportSequence.metadata.language,
                  '',
                ).value;

                const shortTopicGroupName =
                  topicGroupName.length > 18 ? `${topicGroupName.substring(0, 17).trim()}…` : topicGroupName;

                return (
                  <StyledTooltip {...tooltipProps} title={topicGroupName}>
                    <BaseAnswerCell>{shortTopicGroupName}</BaseAnswerCell>
                  </StyledTooltip>
                );
              })}
            </>
          )}
        </HeaderRow>
        {loading && (
          <BoxWithImage className="d-flex justify-content-center" image={<LoadingAnimation />}></BoxWithImage>
        )}
        {!loading && respondents.length === 0 && (
          <BoxWithImage image={<EmptyTable />}>
            <BodyMedium>{t('table:emptyTableLabel')}</BodyMedium>
          </BoxWithImage>
        )}
        {respondents.map((r, i) => {
          const surveys = isHiring ? r.respondent.group.hiringSurveys : r.respondent.group.surveys;
          const channels = surveys?.flatMap((s) => s.publicChannels).filter((ch) => ch.respondentId === r.respId);
          const warningAnswersCount = channels?.filter((ch) => ch.maxAnonymousHighlightedLevel === 1).length;
          const alertAnswersCount = channels?.filter((ch) => ch.maxAnonymousHighlightedLevel > 1).length;

          const rowUrl = `/${accessPath}/${accessToken}/respondent/${r.respId}/${r.seqDate}${urlParams}`;
          const RowWrapper = (!isDetail ? ContentsLink : Fragment) as React.ElementType;
          const rowWrapperProps = !isDetail
            ? {
                to: rowUrl,
                onClick: () => {
                  setScrollToParam(history);
                  pushReportEvent('employee_profile_clicked', 'click', {
                    from_page: currentPage,
                    table_row_position: i + 1,
                  });
                },
              }
            : {};

          return (
            <Row key={`${r.respId}-${r.seqDate || r.firstSurveySentAt}`} data-cy="report-onb-respondents-row">
              {isMobile ? (
                <RowWrapper {...rowWrapperProps}>
                  <RespondentCell
                    data-icom={i === 0 ? 'process-report-feelings-respondent' : undefined}
                    withoutPadding={r.hasUnreadMessage}
                  >
                    {respondentLink ? (
                      <FakeLink>
                        <NameSpan>
                          {r.respondent.respondent.surname} {r.respondent.respondent.firstname}
                        </NameSpan>
                      </FakeLink>
                    ) : (
                      <NameSpan>
                        {r.respondent.respondent.surname} {r.respondent.respondent.firstname}
                      </NameSpan>
                    )}
                    <TeamSpan>
                      {isHiring ? (
                        r.position ? (
                          <Tooltip title={r.position || undefined}>{r.position}</Tooltip>
                        ) : (
                          '-'
                        )
                      ) : (
                        `${t('team')} ${r.teamName.teamName}`
                      )}
                    </TeamSpan>
                    {r.hasUnreadMessage && (
                      <div>
                        <UnreadMessagesLabel color={LabelColor.SUCCESS} size={LabelSize.XSMALL}>
                          {t('unreadMessages')}
                        </UnreadMessagesLabel>
                      </div>
                    )}
                    {(!!warningAnswersCount || !!alertAnswersCount) && (
                      <MobileAlerts>
                        {!!warningAnswersCount && (
                          <>
                            <StyledTooltip {...tooltipProps} title={t('alertsTooltip')}>
                              <>
                                <StyledCommentWarningIcon /> ({warningAnswersCount})
                              </>
                            </StyledTooltip>
                          </>
                        )}
                        {!!alertAnswersCount && (
                          <>
                            <StyledTooltip {...tooltipProps} title={t('alertsTooltip')}>
                              <>
                                <StyledAlertIcon /> ({alertAnswersCount})
                              </>
                            </StyledTooltip>
                          </>
                        )}
                      </MobileAlerts>
                    )}
                  </RespondentCell>
                  <DateCell>
                    {isHiring
                      ? r.firstSurveySentAt
                        ? getLocalizedDateWithoutLeadingZeroes(new Date(r.firstSurveySentAt))
                        : '-'
                      : r.respondent.group.addedOn
                        ? getLocalizedDateWithoutLeadingZeroes(new Date(r.respondent.group.addedOn), i18n.language)
                        : t('deleted')}
                  </DateCell>
                </RowWrapper>
              ) : (
                <>
                  <RowWrapper {...rowWrapperProps}>
                    <DateCell>
                      {isHiring
                        ? r.firstSurveySentAt
                          ? getLocalizedDateWithoutLeadingZeroes(new Date(r.firstSurveySentAt))
                          : '-'
                        : r.respondent.group.addedOn
                          ? getLocalizedDateWithoutLeadingZeroes(new Date(r.respondent.group.addedOn), i18n.language)
                          : t('deleted')}
                    </DateCell>
                    <RespondentCell
                      data-icom={i === 0 ? 'process-report-feelings-respondent' : undefined}
                      withoutPadding={r.hasUnreadMessage}
                    >
                      {respondentLink ? (
                        <FakeLink>
                          <NameSpan>
                            {r.respondent.respondent.surname} {r.respondent.respondent.firstname}
                          </NameSpan>
                        </FakeLink>
                      ) : (
                        <NameSpan>
                          {r.respondent.respondent.surname} {r.respondent.respondent.firstname}
                        </NameSpan>
                      )}
                      <TeamSpan>
                        {isHiring ? (
                          r.position ? (
                            <Tooltip title={r.position || undefined}>{r.position}</Tooltip>
                          ) : (
                            '-'
                          )
                        ) : (
                          `${t('team')} ${r.teamName.teamName}`
                        )}
                      </TeamSpan>
                      {r.hasUnreadMessage && (
                        <div>
                          <Label color={LabelColor.SUCCESS} size={LabelSize.XSMALL}>
                            {t('unreadMessages')}
                          </Label>
                        </div>
                      )}
                    </RespondentCell>
                    {isHiring && <BaseDateCell>{r.recruiterName || '-'}</BaseDateCell>}
                    {isHiring && (
                      <BaseDateCell>{r.isHired ? t('hired') : r.isRejected ? t('rejected') : '-'}</BaseDateCell>
                    )}
                  </RowWrapper>
                  {reportSequence.surveyGroup?.steps.map((step) => {
                    const rs = r.steps?.find((s) => s.step.processStep.id === step!.processStep.id);
                    if (!rs) {
                      return null;
                    }
                    if (rs.metricAnswer?.value !== null && rs.metricAnswer?.value !== undefined) {
                      const result = rs.metricAnswer.value;
                      return (
                        <StyledAnswerCell>
                          <StyledTooltip
                            {...tooltipProps}
                            title={isHiring ? t('feelingHiringQuestion') : t('feelingQuestion')}
                          >
                            <LinkedBaseColorAnswerCell
                              to={rs.detailUrl}
                              data-cy="report-respondents-cell"
                              isPerfect={result > 5}
                              isWarning={result <= 5 && result >= 4}
                              isAlert={result < 4}
                              onClick={(event) => {
                                event.stopPropagation();
                                setScrollToParam(history);
                              }}
                            >
                              <span data-cy="metric-question">{result}</span>
                              {renderAnswerAlerts(rs.maxWeight, true)}
                            </LinkedBaseColorAnswerCell>
                          </StyledTooltip>
                          {rs.unread && isDetail && (
                            <StyledLabel color={LabelColor.SUCCESS} size={LabelSize.XSMALL}>
                              {t('unreadChannel')}
                            </StyledLabel>
                          )}
                        </StyledAnswerCell>
                      );
                    }
                    if (rs.undelivered) {
                      if (
                        !containsAnonymity &&
                        reportSequence.metadata.organization.featureNames?.includes(
                          Features.SendInvitationFromReport,
                        ) &&
                        !isHiring
                      ) {
                        return (
                          <SplitCell>
                            <LinkedHalfOfCell
                              to={rs.detailUrl}
                              data-cy="report-respondents-cell"
                              onClick={(event) => {
                                event.stopPropagation();
                                setScrollToParam(history);
                                setEditRespondent({
                                  respondent: r.respondent.respondent,
                                  contact: rs.undelivered?.contact!,
                                });
                              }}
                            >
                              {rs.isResending ? (
                                <StyledTooltip {...tooltipProps} title={t('deliveryInProgressTooltip')}>
                                  <HourGlassIcon />
                                </StyledTooltip>
                              ) : (
                                <StyledTooltip {...tooltipProps} title={t('notDeliveredTooltip')}>
                                  <NotDeliveredIcon />
                                </StyledTooltip>
                              )}
                            </LinkedHalfOfCell>
                            {rs.survey?.status !== SurveyStatus.Activating && (
                              <HalfOfCell
                                light
                                role="button"
                                tabIndex={0}
                                aria-disabled={clicked}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (rs.survey?.status === SurveyStatus.Ongoing) {
                                    rs.sendAgainSurvey();
                                  } else {
                                    rs.reopenSurvey();
                                  }
                                }}
                              >
                                <StyledTooltip {...tooltipProps} title={t('sendAgainTooltip')}>
                                  <DeprecatedSendIcon color={theme.colors.actionPrimary.default} size={18} />
                                </StyledTooltip>
                              </HalfOfCell>
                            )}
                          </SplitCell>
                        );
                      } else {
                        return (
                          <StyledTooltip {...tooltipProps} title={t('notDeliveredTooltip')}>
                            <LinkedBaseColorAnswerCell
                              to={rs.detailUrl}
                              data-cy="report-respondents-cell"
                              onClick={(event) => {
                                event.stopPropagation();
                                setScrollToParam(history);
                                setEditRespondent({
                                  respondent: r.respondent.respondent,
                                  contact: rs.undelivered?.contact!,
                                });
                              }}
                            >
                              <NotDeliveredIcon />
                            </LinkedBaseColorAnswerCell>
                          </StyledTooltip>
                        );
                      }
                    }
                    if (
                      rs.survey?.status === SurveyStatus.Ongoing &&
                      rs.survey.respondentIds.includes(r.respondent.respondent.id) &&
                      !rs.channel?.finishedAt
                    ) {
                      return (
                        <StyledTooltip {...tooltipProps} title={t('ongoingTooltip')}>
                          <LinkedAnswerCell
                            to={rs.detailUrl}
                            data-cy="report-respondents-cell"
                            onClick={(event) => {
                              event.stopPropagation();
                              setScrollToParam(history);
                            }}
                          >
                            {t('ongoing')}
                            <div style={{ lineHeight: '10px' }}>{'-'}</div>
                            {getLocalizedDateWithoutLeadingZeroes(new Date(rs.survey.endsAt), i18n.language, true)}
                          </LinkedAnswerCell>
                        </StyledTooltip>
                      );
                    }

                    if (
                      (rs.survey &&
                        (([SurveyStatus.Prepared, SurveyStatus.Notstarted, SurveyStatus.Activating].includes(
                          rs.survey.status,
                        ) &&
                          (r.respondent.respondent.deleted ||
                            r.respondent.group.deletedRespondents?.includes(r.respId))) ||
                          ([SurveyStatus.Completed, SurveyStatus.Archived, SurveyStatus.Ongoing].includes(
                            rs.survey.status,
                          ) &&
                            !rs.survey.respondentIds.includes(r.respondent.respondent.id)))) ||
                      !rs.survey
                    ) {
                      if (
                        containsAnonymity ||
                        !reportSequence.metadata.organization.featureNames?.includes(
                          Features.SendInvitationFromReport,
                        ) ||
                        (rs.survey &&
                          (r.respondent.respondent.deleted ||
                            r.respondent.group.deletedRespondents?.includes(r.respId))) ||
                        !rs.survey ||
                        isHiring
                      ) {
                        return (
                          <StyledTooltip {...tooltipProps} title={t('notSentTooltip')}>
                            <LinkedBaseColorAnswerCell
                              to={rs.detailUrl}
                              data-cy="report-respondents-cell"
                              onClick={(event) => {
                                event.stopPropagation();
                                setScrollToParam(history);
                              }}
                            >
                              <NoSurveyIcon data-cy="no-survey-icon" />
                            </LinkedBaseColorAnswerCell>
                          </StyledTooltip>
                        );
                      }
                      return (
                        <SplitCell>
                          <LinkedHalfOfCell
                            to={rs.detailUrl}
                            data-cy="report-respondents-cell"
                            onClick={(event) => {
                              event.stopPropagation();
                              setScrollToParam(history);
                            }}
                          >
                            <StyledTooltip {...tooltipProps} title={t('notSentTooltip')}>
                              <NoSurveyIcon />
                            </StyledTooltip>
                          </LinkedHalfOfCell>
                          {rs.survey?.status !== SurveyStatus.Activating && (
                            <HalfOfCell
                              light
                              role="button"
                              aria-disabled={rs.disabled || clicked}
                              tabIndex={0}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (rs.survey?.status === SurveyStatus.Ongoing) {
                                  rs.sendAgainSurvey();
                                } else if (rs.survey) {
                                  rs.reopenSurvey();
                                } else {
                                  rs.createMissingSurvey();
                                }
                              }}
                            >
                              <StyledTooltip
                                {...tooltipProps}
                                title={t(rs.disabled ? 'todayWasAlreadySent' : 'sendTooltip')}
                              >
                                <DeprecatedSendIcon color={theme.colors.actionPrimary.default} size={18} />
                              </StyledTooltip>
                            </HalfOfCell>
                          )}
                        </SplitCell>
                      );
                    }

                    if (
                      rs.channel &&
                      !rs.channel.withAnswer &&
                      !rs.channel.isFinishedByRespondent &&
                      !containsAnonymity &&
                      reportSequence.metadata.organization.featureNames?.includes(Features.SendInvitationFromReport) &&
                      !r.respondent.group.deletedRespondents?.includes(r.respId) &&
                      !r.respondent.respondent.deleted &&
                      !isHiring
                    ) {
                      return (
                        <SplitCell>
                          <LinkedHalfOfCell
                            to={rs.detailUrl}
                            data-cy="report-respondents-cell"
                            onClick={(event) => {
                              event.stopPropagation();
                              setScrollToParam(history);
                            }}
                          >
                            <StyledTooltip {...tooltipProps} title={t('noAnswer')}>
                              <StyledNoIcon />
                            </StyledTooltip>
                          </LinkedHalfOfCell>
                          <HalfOfCell
                            light
                            role="button"
                            tabIndex={0}
                            aria-disabled={rs.disabled || clicked}
                            onClick={(event) => {
                              event.stopPropagation();
                              rs.reopenSurvey();
                            }}
                            data-cy="send-again-button"
                          >
                            <StyledTooltip
                              {...tooltipProps}
                              title={t(rs.disabled ? 'todayWasAlreadySent' : 'sendAgainTooltip')}
                            >
                              <DeprecatedSendIcon color={theme.colors.actionPrimary.default} size={18} />
                            </StyledTooltip>
                          </HalfOfCell>
                        </SplitCell>
                      );
                    }

                    if (rs.survey && [SurveyStatus.Prepared].includes(rs.survey.status)) {
                      return (
                        <StyledTooltip {...tooltipProps} title={t('preparedTooltip')}>
                          <LinkedAnswerCell
                            to={rs.detailUrl}
                            data-cy="report-respondents-cell"
                            onClick={(event) => {
                              event.stopPropagation();
                              setScrollToParam(history);
                            }}
                          >
                            {getLocalizedDateWithoutLeadingZeroes(new Date(rs.survey.sendAt), i18n.language, true)}
                            <div style={{ lineHeight: '10px' }}>{'-'}</div>
                            {getLocalizedDateWithoutLeadingZeroes(new Date(rs.survey.endsAt), i18n.language, true)}
                          </LinkedAnswerCell>
                        </StyledTooltip>
                      );
                    }

                    if (
                      rs.survey &&
                      !containsAnonymity &&
                      [SurveyStatus.Notstarted].includes(rs.survey.status) &&
                      !isHiring
                    ) {
                      return (
                        <SplitCell>
                          <LinkedHalfOfCell
                            to={rs.detailUrl}
                            data-cy="report-respondents-cell"
                            onClick={(event) => {
                              event.stopPropagation();
                              setScrollToParam(history);
                            }}
                          >
                            <StyledTooltip {...tooltipProps} title={t('notSentTooltip')}>
                              <NoSurveyIcon />
                            </StyledTooltip>
                          </LinkedHalfOfCell>
                          <HalfOfCell
                            light
                            role="button"
                            tabIndex={0}
                            aria-disabled={clicked}
                            onClick={(event) => {
                              event.stopPropagation();
                              rs.updateNotStartedSurvey();
                            }}
                          >
                            <StyledTooltip {...tooltipProps} title={t('sendTooltip')}>
                              <DeprecatedSendIcon color={theme.colors.actionPrimary.default} size={18} />
                            </StyledTooltip>
                          </HalfOfCell>
                        </SplitCell>
                      );
                    }
                    if (!step.hasMetricQuestion && rs.channel?.withAnswer) {
                      return (
                        <StyledAnswerCell>
                          <StyledTooltip {...tooltipProps} title={t('noMetricQuestionTooltip')}>
                            <LinkedBaseColorAnswerCell
                              to={rs.detailUrl}
                              data-cy="report-respondents-cell"
                              onClick={(event) => {
                                event.stopPropagation();
                                setScrollToParam(history);
                              }}
                            >
                              <NoMetricsQuestionIcon data-cy="no-metric-question-icon" />
                              {renderAnswerAlerts(rs.maxWeight)}
                            </LinkedBaseColorAnswerCell>
                          </StyledTooltip>
                          {rs.unread && isDetail && (
                            <StyledLabel color={LabelColor.SUCCESS} size={LabelSize.XSMALL}>
                              {t('unreadChannel')}
                            </StyledLabel>
                          )}
                        </StyledAnswerCell>
                      );
                    }

                    if (step.hasMetricQuestion && rs.channel?.withAnswer) {
                      return (
                        <StyledAnswerCell>
                          <StyledTooltip {...tooltipProps} title={t('answeredWithoutMetricAnswerTooltip')}>
                            <LinkedBaseColorAnswerCell
                              to={rs.detailUrl}
                              data-cy="report-respondents-cell"
                              onClick={(event) => {
                                event.stopPropagation();
                                setScrollToParam(history);
                              }}
                            >
                              <AnsweredWithoutMetricAnswerIcon data-cy="answered-without-metric-answer-icon" />
                              {renderAnswerAlerts(rs.maxWeight)}
                            </LinkedBaseColorAnswerCell>
                          </StyledTooltip>
                          {rs.unread && isDetail && (
                            <StyledLabel color={LabelColor.SUCCESS} size={LabelSize.XSMALL}>
                              {t('unreadChannel')}
                            </StyledLabel>
                          )}
                        </StyledAnswerCell>
                      );
                    }
                    return (
                      <StyledAnswerCell>
                        <StyledTooltip {...tooltipProps} title={t('noAnswer')}>
                          <LinkedBaseColorAnswerCell
                            to={rs.detailUrl}
                            data-cy="report-respondents-cell"
                            onClick={(event) => {
                              event.stopPropagation();
                              setScrollToParam(history);
                            }}
                          >
                            <StyledNoIcon />
                            {renderAnswerAlerts(rs.maxWeight)}
                          </LinkedBaseColorAnswerCell>
                        </StyledTooltip>
                        {rs.unread && isDetail && (
                          <StyledLabel color={LabelColor.SUCCESS} size={LabelSize.XSMALL}>
                            {t('unreadChannel')}
                          </StyledLabel>
                        )}
                      </StyledAnswerCell>
                    );
                  })}
                  {showDeleteButton &&
                    !(
                      r.respondent.respondent.deleted ||
                      r.respondent.group.deletedRespondents?.includes(r.respId) ||
                      surveys?.length === 0
                    ) &&
                    !isHiring && (
                      <Tooltip
                        title={t(r.isDeleteDisabled ? 'removeRespondentDisabledTooltip' : 'removeRespondentTooltip')}
                      >
                        <StyledCloseIcon
                          key="close"
                          aria-label="Close"
                          onClick={(event) => {
                            event.stopPropagation();
                            showModal(r.respondent.respondent, r.respondent);
                          }}
                          disabled={r.isDeleteDisabled}
                          data-cy="delete-respondent-icon"
                        >
                          <DeleteIcon />
                        </StyledCloseIcon>
                      </Tooltip>
                    )}
                </>
              )}
            </Row>
          );
        })}
      </Container>
      <RemoveRespondentModal
        show={modal}
        showModal={setModal}
        respondent={respondentToDelete}
        creditsToReturn={
          groupedRespondents
            .find((respondent) => respondent.respondents.find((resp) => resp.id === respondentToDelete?.id))
            ?.creditsToReturn?.find((creditsToReturn) => creditsToReturn.respondentId === respondentToDelete?.id)
            ?.creditsToReturn || undefined
        }
        dateFrom={dateFrom}
        surveyGroupId={reportSequence.surveyGroup!.id}
        accessToken={accessToken}
        onReload={onReload}
        surveysWithAnswers={groupedRespondents
          .find((respondent) => respondent.respondents.find((resp) => resp.id === respondentToDelete?.id))
          ?.surveys?.filter((survey) =>
            survey.publicChannels.find(
              (channel) => channel.withAnswer && channel.respondentId === respondentToDelete?.id,
            ),
          )}
      />
    </>
  );
};

export default RespondentsFeelings;

const FakeLink = styled.span`
  font-weight: ${theme.typography.body.medium.button.fontWeight};
  color: ${theme.colors.actionPrimary.default};
  text-decoration: none;
  cursor: pointer;
`;
